import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { authAPI } from '../utils/api';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Logo from '../components/Logo';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('email');
  const [step, setStep] = useState('request'); // request, verify, success
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    otp: '',
    new_password: '',
    confirm_password: ''
  });
  const [resetToken, setResetToken] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = activeTab === 'email' 
        ? { email: formData.email }
        : { phone: formData.phone };

      const response = await authAPI.forgotPassword(payload);
      
      if (activeTab === 'email' && response.data.reset_token) {
        // For email, we might have token in response (sandbox mode)
        setResetToken(response.data.reset_token);
        setStep('verify');
        toast.success('Reset instructions sent! Enter your new password below.');
      } else if (activeTab === 'phone') {
        // For phone, move to OTP verification
        setStep('verify');
        toast.success('OTP sent to your phone number!');
      } else {
        toast.success(response.data.message);
        setTimeout(() => navigate('/login'), 3000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (formData.new_password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const payload = activeTab === 'email'
        ? { token: resetToken, new_password: formData.new_password }
        : { phone: formData.phone, otp: formData.otp, new_password: formData.new_password };

      const response = await authAPI.resetPassword(payload);
      
      toast.success('Password reset successful!');
      setStep('success');
      
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-md card">
        <CardHeader className="text-center">
          <div className="mb-4 flex justify-center">
            <Logo size="md" showText={false} />
          </div>
          <CardTitle className="text-3xl heading">
            {step === 'success' ? 'Success!' : 'Reset Password'}
          </CardTitle>
          <CardDescription>
            {step === 'request' && 'Enter your email or phone to reset password'}
            {step === 'verify' && 'Enter verification details and new password'}
            {step === 'success' && 'Your password has been reset successfully'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'request' && (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="email">Email</TabsTrigger>
                <TabsTrigger value="phone">Phone</TabsTrigger>
              </TabsList>
              
              <TabsContent value="email">
                <form onSubmit={handleRequestReset} className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="phone">
                <form onSubmit={handleRequestReset} className="space-y-4">
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+91 XXXXXXXXXX"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Sending...' : 'Send OTP'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          )}

          {step === 'verify' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              {activeTab === 'phone' && (
                <div>
                  <Label htmlFor="otp">OTP</Label>
                  <Input
                    id="otp"
                    type="text"
                    placeholder="Enter 6-digit OTP"
                    value={formData.otp}
                    onChange={(e) => setFormData({ ...formData, otp: e.target.value })}
                    required
                    maxLength={6}
                  />
                </div>
              )}
              <div>
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  type="password"
                  placeholder="Enter new password"
                  value={formData.new_password}
                  onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="confirm_password">Confirm Password</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  placeholder="Confirm new password"
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}

          {step === 'success' && (
            <div className="text-center py-8">
              <div className="mb-4">
                <svg className="w-16 h-16 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-gray-600 mb-4">
                Your password has been reset successfully. Redirecting to login...
              </p>
            </div>
          )}

          {step === 'request' && (
            <p className="text-center mt-4 text-sm text-gray-600">
              Remember your password?{' '}
              <Button variant="link" onClick={() => navigate('/login')} className="p-0">
                Sign in
              </Button>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
